import React from 'react'
import { observer } from 'mobx-react'
import sample from 'lodash/sample'
import { observable, action } from 'mobx'
import BodyClass from 'react-body-classname'
import cx from 'classnames'
import Helmet from 'react-helmet'
import './styles.sass'

const IOS_APP = 'https://itunes.apple.com/us/app/taxfyle-personal-and-business-tax-return-filing/id1058033104'
const ANDROID_APP = 'https://play.google.com/store/apps/details?id=com.taxfyle.TaxfyleMobile'

export default observer(function LandingPage ({
  onSignupClick,
  onLoginClick,
  partnerLogo,
  hideLogin,
  title = 'Welcome to Taxfyle',
  callToActionLink,
  additionalCtaParagraph,
  hideAppLinks
}) {
  return (
    <div className="landing-page">
      <Helmet title={title} />
      <BodyClass className="landing-page-body" />
      <header className="landing-header">
        <div className="container">
          <nav className="nav">
            <div className="nav-left">
              <a className="nav-item is-brand" href="#hello">
                <img className="landing-header-logo" src={require('./img/logo-blue.png')} alt="Taxfyle" />
                {partnerLogo && <span className="plus"> + </span>}
                {partnerLogo && <img className="landing-header-logo partner-logo" src={partnerLogo} />}
              </a>
            </div>

            <div className="nav-center is-hidden-mobile">
              <a href="#why-taxfyle" className="nav-item has-hover">
                Why Taxfyle?
              </a>
              <a href="#how-it-works" className="nav-item has-hover">
                How It Works
              </a>
              <a href="https://intercom.help/taxfyle" target="_blank" rel="noopener noreferrer" className="nav-item has-hover">
                FAQ
              </a>
            </div>
            <div className="nav-right">
              <span className="nav-item">
                <a href={callToActionLink} onClick={!callToActionLink && onSignupClick} className="button is-cta is-header-button">
                  Try now
                </a>
              </span>
              {!hideLogin &&
                <a onClick={onLoginClick} className="nav-item has-hover">Sign in</a>
              }
            </div>
          </nav>
        </div>
      </header>

      <section className="landing-hero container" id="hello">
        <div className="landing-hero-content">
          <div className="hero-primary-block">
            <h1 className="hero-title">Have your taxes done for you.</h1>
            <p>
              <strong>Taxfyle</strong> connects you with a tax expert who does your taxes,
              so you won't have to.
            </p>
            {additionalCtaParagraph}
          </div>

          <div className="hero-cta-section">
            <a
              href={callToActionLink}
              className="button is-cta is-hero-cta"
              onClick={!callToActionLink && onSignupClick}
            >
              Try now
            </a>
          </div>
          {!hideAppLinks &&
            <div className="hero-app-section">
              Get the app on <a href={IOS_APP} target="_blank" rel="noopener noreferrer">iOS</a>, <a href={ANDROID_APP} target="_blank" rel="noopener noreferrer">Android</a>
            </div>
          }
        </div>
        <div className="landing-hero-img">
          <img src={require('./img/devices.png')} />
        </div>
      </section>

      <section className="section">
        <h2 className="section-title" id="why-taxfyle">Why Taxfyle?</h2>
        <div className="section-content">
          <p>
            We're faster, cheaper, and easier than everyone else - our customers love us for it. 😍
          </p>
        </div>
        <div className="has-spacing-top--less">
          <ReviewBox/>
        </div>
        <PressImages />
        <AppleBestApp/>
      </section>

      <section className="section">
        <div className="h2 section-title" id="how-it-works">How It Works</div>
        <div className="section-content">
          The hardest part is figuring out what to do with all that free time. 😎
        </div>

        <div className="steps">
          <div className="step">
            <div className="step-info">
              <div className="step-title">Step 1</div>
              <div className="step-text">
                Answer a few simple questions. 🤔
              </div>
            </div>
            <div className="step-image">
              <img src={require('./img/step1.png')} />
            </div>
          </div>

          <div className="step">
            <div className="step-info push-down">
              <div className="step-title">Step 2</div>
              <div className="step-text">
                Review and submit your order. 👀
              </div>
            </div>
            <div className="step-image">
              <img src={require('./img/step2.png')} />
            </div>
          </div>

          <div className="step">
            <div className="step-info">
              <div className="step-title">Step 3</div>
              <div className="step-text">
                Upload your tax documents & say hello to your Tax Pro — they'll make sure you get that fat refund you deserve. 💰
              </div>
            </div>
            <div className="step-image">
              <img src={require('./img/step3.png')} />
            </div>
          </div>

          <div className="step">
            <div className="step-info">
              <div className="step-title">Step 4</div>
              <div className="step-text">
                Review &amp; sign your return - done! ✅
              </div>
            </div>
            <div className="step-image">
              <span className="step-emoji">
                🎉 🎊
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">Get your free quote!</h2>
        <div className="section-content">
          <p>
            Sign up &amp; answer a few questions to get a free quote - no strings attached. It only takes a minute.
          </p>
        </div>
        <div className="section-cta-container">
          <a className="button is-cta is-section-cta" href={callToActionLink} onClick={!callToActionLink && onSignupClick}>
            Try now
          </a>
          <div className="section-cta-subtitle">
            You won't regret it. 👍
          </div>
        </div>
      </section>

      <footer className="landing-footer container">
        <div className="footer-items">
          <div className="footer-logo">
            <img src={require('./img/logo-icon.png')} alt="Taxfyle" />
          </div>
          <div className="footer-links">
            <a href="https://taxfyle.com/partners" target="_blank" rel="noopener noreferrer">Partners</a>
            <a href="https://intercom.help/taxfyle" target="_blank" rel="noopener noreferrer">FAQ</a>
            <a href="https://taxfyle.com/policies/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
            <a href="https://taxfyle.com/policies/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <a href="https://taxfyle.com/apply" target="_blank" rel="noopener noreferrer">Pro Apply</a>
          </div>
          <div className="footer-socialmedia">
            <SocialIcon icon="facebook" link="https://facebook.com/Taxfyle" />
            <SocialIcon icon="twitter" link="https://twitter.com/Taxfyle" />
            <SocialIcon icon="instagram" link="https://instragram.com/Taxfyle" />
            <SocialIcon icon="youtube" link="https://www.youtube.com/channel/UChipeQJrmT2-qqZCnSidjuw" />
          </div>
          <div className="footer-copyright">
            Copyright &copy; Tickmark, Inc {new Date().getFullYear()}
          </div>
        </div>
      </footer>
    </div>
  )
})

const AppleBestApp = () => (
  <div className="apple-best-app">
    <div className="ornament">
      <img src={require('./img/ornament-left.png')} />
    </div>
    <div className="text">
      <p className="best-app-title">2017 #1 Tax Time App</p>
      <p className="best-app-subtitle">- Apple</p>
    </div>
    <div className="ornament">
      <img src={require('./img/ornament-right.png')} />
    </div>
  </div>
)

const testimonials = [
  { quote: 'Kurt F. was great to work with and we will be using Taxfyle again for our 2017 taxes.', username: 'Matthew S' },
  { quote: 'Fantastic service and he saved me $1000! Thanks so much. ', username: 'Mayuka K' },
  { quote: 'The experience was quick and easy!  I\'ll do it again next year! John was great!', username: 'Terri M' },
  { quote: 'Freddie was fantastic! He was extremely responsive. He thoroughly answered all of my many questions. This process was made so much easier. Thank you Freddie and the taxfyle team for putting my mind at ease! I no longer have to worry if I\'m doing my taxes correctly. What an amazing app.', username: 'Haley A' },
  { quote: 'Gilbert was super helpful and did a great job of explaining everything clearly, thank you!', username: 'Jennifer A' },
  { quote: 'To the point advice, and directions. Very responsive and courteous. Definitely would recommend him.', username: 'Ruben A' },
  { quote: 'Joe knows what he\'s doing, very efficient service and overall experience! I will be coming back for future returns.', username: 'Jason T' },
  { quote: 'Thank you for the great turnaround time!', username: 'Vastal P' },
  { quote: 'This was my first time filing for a business. Stewart was excellent with knowledge and wonderful with patience as he helped guide me through. Thank you very much Taxfyle.', username: 'Gordon R' }
]

@observer
class ReviewBox extends React.Component {
  @observable review

  componentWillMount () {
    const index = testimonials.indexOf(sample(testimonials))
    this.setReview(index)
  }

  @action setReview (index) {
    this.review = testimonials[index]
  }

  render () {
    return (
      <div className="review-container">
        <div className="review-box">
          <blockquote className={cx('review-text', `review-size-${this.size}`)}>
            {this.review.quote}
            &nbsp;&ndash; {this.review.username}
          </blockquote>

          <div className="pager">
            {testimonials.map((r, i) =>
              <a
                className={cx('pager-item', this.review === r && 'is-current')}
                key={i}
                onClick={e => e.preventDefault() || this.setReview(i)}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
}

const PressImages = () => (
  <div className="press-images">
    {['logo-forbes', 'logo-businessinsider', 'logo-macworld', 'logo-venturebeat', 'logo-yahoo'].map(p =>
      <div className="press-logo" key={p}>
        <img src={require(`./img/${p}.png`)} alt={p} />
      </div>
    )}
  </div>
)

const SocialIcon = ({ link, icon }) => (
  <a className="social-icon" href={link} target="_blank" rel="noopener noreferrer">
    <i className={`fa fa-${icon}`}></i>
  </a>
)



// WEBPACK FOOTER //
// ./src/client/components/LandingPage/index.jsx